export type ChatTerminalStatus = "completed" | "interrupted" | "failed";

export type ChatControlSnapshot = {
  threadId: string | null;
  turnId: string | null;
  submitting: boolean;
  interruptPending: boolean;
  canStop: boolean;
  terminalStatus: ChatTerminalStatus | null;
  error: string;
};

type Listener = (snapshot: ChatControlSnapshot) => void;

function terminalStatus(value: unknown): ChatTerminalStatus | null {
  return value === "completed" || value === "interrupted" || value === "failed" ? value : null;
}

/**
 * Consumer Chat does not expose a public OpenAI RPC. This store therefore
 * models the client-side contract an explicitly advertised gateway adapter
 * must honor: one active run, one interrupt request, and server-authoritative
 * terminal state.
 */
export class ChatControlStore {
  private state: ChatControlSnapshot = {
    threadId: null,
    turnId: null,
    submitting: false,
    interruptPending: false,
    canStop: false,
    terminalStatus: null,
    error: "",
  };
  private readonly listeners = new Set<Listener>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): ChatControlSnapshot {
    return { ...this.state };
  }

  beginSubmission(): boolean {
    if (this.state.submitting || this.state.turnId || this.state.interruptPending) return false;
    this.state.submitting = true;
    this.state.terminalStatus = null;
    this.state.error = "";
    this.emit();
    return true;
  }

  submissionAccepted(threadId: string, turnId: string): void {
    if (!threadId || !turnId) return;
    // A terminal event may legitimately overtake the RPC response. Never
    // revive a run the server has already declared terminal.
    if (this.state.terminalStatus && !this.state.turnId) {
      this.state.submitting = false;
      this.emit();
      return;
    }
    this.state.threadId = threadId;
    this.state.turnId = turnId;
    this.state.submitting = false;
    this.state.interruptPending = false;
    this.state.canStop = true;
    this.state.terminalStatus = null;
    this.state.error = "";
    this.emit();
  }

  submissionCompleted(threadId: string): void {
    this.state.threadId = threadId || this.state.threadId;
    this.state.turnId = null;
    this.state.submitting = false;
    this.state.interruptPending = false;
    this.state.canStop = false;
    this.state.terminalStatus = "completed";
    this.state.error = "";
    this.emit();
  }

  submissionFailed(message: string): void {
    this.state.submitting = false;
    this.state.error = message;
    this.emit();
  }

  submissionUncertain(message: string): void {
    // Keep the composer locked while the gateway replays the same
    // clientMessageId. Unlocking here would allow a second semantic request
    // while the first one may already be running on the server.
    this.state.submitting = true;
    this.state.error = message;
    this.emit();
  }

  runStarted(threadId: string, turnId: string): void {
    if (!threadId || !turnId) return;
    this.state.threadId = threadId;
    this.state.turnId = turnId;
    this.state.submitting = false;
    this.state.interruptPending = false;
    this.state.canStop = true;
    this.state.terminalStatus = null;
    this.state.error = "";
    this.emit();
  }

  beginInterrupt(): { threadId: string; turnId: string } | null {
    if (!this.state.threadId || !this.state.turnId || this.state.interruptPending) return null;
    this.state.interruptPending = true;
    this.state.canStop = false;
    this.emit();
    return { threadId: this.state.threadId, turnId: this.state.turnId };
  }

  interruptRequestAccepted(threadId: string, turnId: string): void {
    if (this.state.threadId !== threadId || this.state.turnId !== turnId || !this.state.interruptPending) return;
    // The run remains active until its authoritative completed event.
    this.state.canStop = false;
    this.emit();
  }

  interruptRequestFailed(threadId: string, turnId: string, message: string): void {
    if (this.state.threadId !== threadId || this.state.turnId !== turnId) return;
    this.state.interruptPending = false;
    this.state.canStop = true;
    this.state.error = message;
    this.emit();
  }

  runCompleted(threadId: string, turnId: string, value: unknown, message = ""): boolean {
    const status = terminalStatus(value);
    if (!status) return false;
    if (this.state.threadId && threadId && this.state.threadId !== threadId) return false;
    if (this.state.turnId && turnId && this.state.turnId !== turnId) return false;
    this.state.threadId = threadId || this.state.threadId;
    this.state.turnId = null;
    this.state.submitting = false;
    this.state.interruptPending = false;
    this.state.canStop = false;
    this.state.terminalStatus = status;
    this.state.error = status === "failed" ? message : "";
    this.emit();
    return true;
  }

  reset(): void {
    this.state = {
      threadId: null,
      turnId: null,
      submitting: false,
      interruptPending: false,
      canStop: false,
      terminalStatus: null,
      error: "",
    };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}
